'use client';

import { useCallback, useEffect, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useCopy } from '@/app/hooks';
import { cn } from '@/app/utils';
import type { BlogPost } from './BlogList';

interface SocialShareButtonProps {
  post: BlogPost;
  currentSlug: string;
  className?: string;
}

type SocialPlatform = 'Facebook' | 'X' | 'Pinterest' | 'LinkedIn' | 'CopyLink';

interface SocialConfig {
  href: string;
  icon: React.ReactNode;
  label: string;
}

const SOCIAL_ICONS = {
  Facebook: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.3074 4.11508V6.65364H14.7978C13.615 6.65364 13.3941 7.22105 13.3941 8.03831V9.85561H16.2114L15.8366 12.7019H13.3941V20H10.4518V12.7019H8V9.85561H10.4518V7.75953C10.4518 5.32682 11.9423 4 14.1153 4C15.1535 4 16.0483 4.07693 16.308 4.11508H16.3074Z"
        fill="currentColor"
      />
    </svg>
  ),
  X: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 7.53887C19.3981 7.80137 18.7616 7.97583 18.11 8.05687C18.7967 7.64949 19.3101 7.00443 19.553 6.24387C18.91 6.6252 18.2065 6.89374 17.473 7.03787C17.0232 6.55765 16.4395 6.22379 15.7975 6.07966C15.1555 5.93554 14.485 5.98781 13.8732 6.22969C13.2613 6.47157 12.7363 6.89188 12.3664 7.436C11.9965 7.98013 11.7988 8.62292 11.799 9.28087C11.799 9.54087 11.821 9.79087 11.875 10.0289C10.5701 9.96489 9.29336 9.62621 8.1283 9.03498C6.96325 8.44375 5.93609 7.61328 5.114 6.59787C4.69282 7.31973 4.56261 8.17498 4.74988 8.98947C4.93716 9.80396 5.42783 10.5165 6.122 10.9819C5.60276 10.9679 5.09447 10.8294 4.64 10.5779V10.6139C4.64087 11.3713 4.90267 12.1053 5.38132 12.6923C5.85997 13.2793 6.52625 13.6835 7.268 13.8369C6.98739 13.9108 6.69819 13.9471 6.408 13.9449C6.19959 13.9486 5.99139 13.9298 5.787 13.8889C5.99894 14.5399 6.40749 15.1092 6.95639 15.5184C7.5053 15.9276 8.16756 16.1566 8.852 16.1739C7.69094 17.0819 6.25897 17.5745 4.785 17.5729C4.516 17.5729 4.258 17.5609 4 17.5279C5.49988 18.4944 7.24768 19.0057 9.032 18.9999C15.068 18.9999 18.368 13.9999 18.368 9.66587C18.368 9.52087 18.363 9.38087 18.356 9.24187C19.003 8.77885 19.5601 8.20175 20 7.53887Z"
        fill="currentColor"
      />
    </svg>
  ),
  Pinterest: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_86_2605)">
        <path
          d="M6.00122 9.74285C6.00122 9.07099 6.14199 8.40392 6.42514 7.74325C6.70828 7.08258 7.113 6.46831 7.64089 5.90362C8.16879 5.33893 8.86145 4.87982 9.72048 4.52789C10.5795 4.17596 11.5313 4 12.5759 4C14.2716 4 15.6633 4.5231 16.7511 5.56769C17.8389 6.61228 18.3827 7.84403 18.3827 9.26295C18.3827 11.0866 17.922 12.5935 16.999 13.782C16.076 14.9706 14.8842 15.5657 13.4237 15.5657C12.9438 15.5657 12.4927 15.4537 12.072 15.2298C11.6513 15.0058 11.3554 14.7339 11.1842 14.4139L10.5443 16.9414C10.4915 17.1446 10.4211 17.3493 10.3364 17.5573C10.2516 17.7652 10.1572 17.9652 10.0564 18.1572C9.95563 18.3491 9.85325 18.5299 9.75247 18.7011C9.65169 18.8722 9.54931 19.0322 9.44853 19.181C9.34775 19.3297 9.25977 19.4561 9.18458 19.5569C9.1094 19.6577 9.04061 19.7456 8.97663 19.8208L8.88064 19.9488C8.84865 19.992 8.80546 20.008 8.75267 19.9968C8.69988 19.9856 8.66789 19.9536 8.65669 19.9008C8.65669 19.8896 8.64869 19.816 8.63269 19.6769C8.6167 19.5377 8.6007 19.3921 8.5847 19.237C8.56871 19.0818 8.55271 18.8818 8.53671 18.6371C8.52072 18.3923 8.51592 18.154 8.52072 17.9252C8.52552 17.6965 8.54151 17.4421 8.56871 17.1654C8.5959 16.8886 8.63589 16.6327 8.68868 16.3975C8.80546 15.8968 9.20058 14.2268 9.87245 11.3905C9.78766 11.2194 9.71728 11.0146 9.66449 10.7746C9.6117 10.5347 9.5845 10.3395 9.5845 10.1908L9.56851 9.95081C9.56851 9.26775 9.74127 8.69986 10.0884 8.24715C10.4355 7.79444 10.8531 7.56729 11.3442 7.56729C11.7393 7.56729 12.0448 7.69846 12.264 7.95921C12.4831 8.21996 12.5919 8.54789 12.5919 8.94301C12.5919 9.18776 12.5471 9.4901 12.4559 9.84683C12.3647 10.2036 12.2448 10.6147 12.096 11.0786C11.9472 11.5425 11.8401 11.9184 11.7761 12.2064C11.6689 12.6863 11.7633 13.1022 12.056 13.4541C12.3488 13.806 12.7407 13.982 13.2318 13.982C14.0748 13.982 14.7707 13.5053 15.3194 12.5503C15.868 11.5953 16.1432 10.4419 16.1432 9.08698C16.1432 8.05199 15.8073 7.20736 15.1354 6.55149C14.4635 5.89562 13.5245 5.56769 12.32 5.56769C10.9762 5.56769 9.88524 5.9996 9.04861 6.86343C8.21198 7.72725 7.79286 8.76225 7.79286 9.96681C7.79286 10.6819 7.99602 11.2833 8.40074 11.7744C8.53991 11.9344 8.5815 12.1056 8.52872 12.2863C8.50792 12.3391 8.47593 12.4623 8.43273 12.6543C8.38954 12.8462 8.35755 12.9694 8.33675 13.0222C8.31596 13.139 8.26157 13.2174 8.17679 13.2541C8.092 13.2909 7.99602 13.2893 7.88884 13.2462C7.26017 12.9902 6.78826 12.5503 6.47313 11.9264C6.15799 11.3025 6.00122 10.5747 6.00122 9.74285Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_86_2605">
          <rect width="12.384" height="16" fill="white" transform="translate(6 4)" />
        </clipPath>
      </defs>
    </svg>
  ),
  LinkedIn: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4.05333 8.56889H7.48444V19.9733H4.05333V8.56889ZM5.77778 4C5.30628 4 4.8541 4.1873 4.5207 4.5207C4.1873 4.8541 4 5.30628 4 5.77778C4.02503 6.23212 4.22332 6.65958 4.55404 6.97211C4.88477 7.28465 5.32274 7.45847 5.77778 7.45778C6.23281 7.45847 6.67079 7.28465 7.00151 6.97211C7.33224 6.65958 7.53053 6.23212 7.55556 5.77778C7.55556 5.30628 7.36825 4.8541 7.03486 4.5207C6.70146 4.1873 6.24927 4 5.77778 4ZM18.8889 10.08C18.5411 9.67898 18.1044 9.36484 17.6136 9.16265C17.1228 8.96047 16.5915 8.87585 16.0622 8.91556C15.3781 8.89965 14.7025 9.06987 14.1076 9.40804C13.5127 9.7462 13.0208 10.2396 12.6844 10.8356H12.6311V8.56889H9.75111C9.80444 9.73333 9.75111 19.9733 9.75111 19.9733H13.1822V12.8889C13.2609 12.4993 13.478 12.1514 13.7934 11.9096C14.1088 11.6677 14.5011 11.5483 14.8978 11.5733C16.0089 11.5733 16.56 12.3822 16.56 13.9467V20H20V13.5111C20.0689 12.2691 19.6728 11.0459 18.8889 10.08Z"
        fill="currentColor"
      />
    </svg>
  ),
  CopyLink: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.9403 7.43842C12.3893 7.65291 12.8101 7.94611 13.182 8.31802C14.9393 10.0754 14.9393 12.9246 13.182 14.682L8.68198 19.182C6.92462 20.9393 4.07538 20.9393 2.31802 19.182C0.56066 17.4246 0.56066 14.5754 2.31802 12.818L4.07499 11.061M17.425 10.439L19.182 8.68198C20.9393 6.92462 20.9393 4.07538 19.182 2.31802C17.4246 0.56066 14.5754 0.56066 12.818 2.31802L8.31802 6.81802C6.56066 8.57538 6.56066 11.4246 8.31802 13.182C8.68992 13.5539 9.11073 13.8471 9.55974 14.0616"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const SOCIAL_COLORS = {
  Facebook: 'group-hover:bg-[#1877F2] group-hover:border-[#1877F2]',
  X: 'group-hover:bg-black group-hover:border-black',
  Pinterest: 'group-hover:bg-[#E60023] group-hover:border-[#E60023]',
  LinkedIn: 'group-hover:bg-[#0A66C2] group-hover:border-[#0A66C2]',
  CopyLink: 'group-hover:bg-gray-900 group-hover:border-gray-900',
} as const;

const getSocialConfig = (shareUrl: string, title: string): Record<SocialPlatform, SocialConfig> => ({
  Facebook: {
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    icon: SOCIAL_ICONS.Facebook,
    label: 'share to Facebook',
  },
  X: {
    href: `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`,
    icon: SOCIAL_ICONS.X,
    label: 'share to X',
  },
  Pinterest: {
    href: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(title)}`,
    icon: SOCIAL_ICONS.Pinterest,
    label: 'share to Pinterest',
  },
  LinkedIn: {
    href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`,
    icon: SOCIAL_ICONS.LinkedIn,
    label: 'share to LinkedIn',
  },
  CopyLink: {
    href: shareUrl,
    icon: SOCIAL_ICONS.CopyLink,
    label: 'link copied',
  },
});

const ShareButton = ({
  config,
  onClick,
  platform,
}: {
  config: SocialConfig;
  onClick: (e: React.MouseEvent, href: string) => void;
  platform: SocialPlatform;
}) => {
  const className = 'group relative block h-11 w-11 cursor-pointer';
  const inner = (
    <>
      <div
        className={cn('absolute inset-0 rounded-full border border-black transition-all', SOCIAL_COLORS[platform])}
      />
      <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 text-black transition-colors group-hover:text-white">
        {config.icon}
      </div>
    </>
  );

  if (platform === 'CopyLink') {
    return (
      <button type="button" className={className} onClick={(e) => onClick(e, config.href)} aria-label={config.label}>
        {inner}
      </button>
    );
  }

  return (
    <a
      href={config.href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={className}
      onClick={(e) => onClick(e, config.href)}
      aria-label={config.label}
    >
      {inner}
    </a>
  );
};

export function SocialShareButton({ post, currentSlug, className }: SocialShareButtonProps) {
  const shareUrl = `https://www.vidu.com/blog/${currentSlug}`;
  const socialConfig = getSocialConfig(shareUrl, post.title);
  const { copy } = useCopy();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const check = () => {
      const hero = document.querySelector('[data-blog-hero]');
      const breadcrumbs = document.querySelector('[data-blog-breadcrumbs]');
      const offset = breadcrumbs ? breadcrumbs.getBoundingClientRect().bottom : 0;
      setVisible(hero ? hero.getBoundingClientRect().bottom <= offset : false);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, []);

  const handleShare = useCallback(
    async (e: React.MouseEvent, href: string) => {
      e.preventDefault();
      if (href === shareUrl) {
        await copy(href);
        toast({
          title: 'link copied',
        });
      } else {
        window.open(
          href,
          'share-dialog',
          'width=800,height=600,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes',
        );
      }
    },
    [shareUrl],
  );

  return (
    <div
      className={cn(
        'inline-flex h-64 flex-col items-start justify-center gap-3 transition-opacity duration-300',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
        className,
      )}
    >
      {(Object.entries(socialConfig) as [SocialPlatform, SocialConfig][]).map(([platform, config]) => (
        <ShareButton key={platform} config={config} onClick={handleShare} platform={platform} />
      ))}
    </div>
  );
}
